import { useOwnershipLabels } from "../../OwnershipLabels";
// KycViewOwnership.jsx

import { OwnershipTreeUaeReadOnly } from "../common/OwnershipTreeUae";
import { buildLiveReviewOwnershipSnapshot } from "../review/KycReviewOwnershipSection";
import { clientCarriesPercent } from "../../../utils/editOwnershipPercentage";

import React from "react";
import { Badge, Pills } from "./ViewUI";
import Lottie from "lottie-react";

import { toIso3 } from "../../../utils/iso2to3";

import iconEntityX from "../../../images/building-lottie.json";
import iconIndividualX from "../../../images/building-lottie.json";

import iconIndividual from "../../../images/avatar.svg";
import iconEntity from "../../../images/businessTrade.svg";
import iconTrust from "../../../images/certificate.svg";

import iconAddress from "../../../images/earth-location.svg";
import iconPassport from "../../../images/passport.svg";
import iconBo from "../../../images/buildpers.svg";
import iconPercent from "../../../images/analytics.svg";
import iconQualifiedUbo from "../../../images/qualified-g.svg";

import iconSettlor from "../../../images/settlor.svg";
import iconTrustee from "../../../images/handshake.svg";
import iconBeneficiary from "../../../images/beneficiary.svg";
import iconProtector from "../../../images/settlor.svg";
import { normalizeSignatoryCapacity } from "../common/signatoryCapacity";
import {
  getQualifiedUboNodeIds,
  isQualifiedUboRow,
} from "../../../utils/qualifiedUboMarkers";

function safeArr(v) {
  return Array.isArray(v) ? v : [];
}

function normType(v) {
  const s = String(v || "")
    .trim()
    .toLowerCase();

  if (
    s === "entity" ||
    s === "company" ||
    s === "legal person" ||
    s === "corporate"
  ) {
    return "entity";
  }

  if (
    s === "individual" ||
    s === "person" ||
    s === "natural person" ||
    s === "private individual"
  ) {
    return "individual";
  }

  if (s === "trust") {
    return "trust";
  }

  if (s === "foundation") {
    return "foundation";
  }

  return "";
}

function countryCodeToName(code) {
  const c = String(code || "")
    .trim()
    .toUpperCase();
  if (!c) return "";

  try {
    const dn = new Intl.DisplayNames(["en"], { type: "region" });
    return dn.of(c) || c;
  } catch {
    return c;
  }
}

function firstNonEmpty(...vals) {
  for (const v of vals) {
    if (v === null || v === undefined) continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return "";
}

function formatPct(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isFinite(n)) return `${n}%`;
  return String(v);
}

function personName(x) {
  return firstNonEmpty(x?.name, x?.fullName, x?.label);
}

// function getPartyNationality(x) {
//   return String(x?.nationality || "").trim();
// }

function getPct(x) {
  return x?.ownershipPercent ?? "";
}

function toNumberOrNull(v) {
  const s = String(v ?? "").trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function shortCountry_ISO2_old(code) {
  return (
    String(code || "")
      .trim()
      .toUpperCase() || "—"
  );
}

function shortCountry(code) {
  return toIso3(String(code || "").trim()) || "—";
}

function shortBasisOfControl(v) {
  const s = String(v || "").trim();
  if (s === "Direct ownership") return "Direct own.";
  if (s === "Indirect ownership") return "Indirect own.";
  if (s === "Managing director") return "Managing dir.";
  if (s === "Control via agreement") return "Control via agr.";
  if (s === "Other") return "Other";
  return s || "—";
}

// LEGAL ARRANGEMENTS

function arrangementTypeLabel(v) {
  const s = String(v || "").trim();
  return s || "Legal arrangement";
}

function arrangementIcon(type) {
  const t = String(type || "")
    .trim()
    .toLowerCase();
  if (t === "trust" || t === "foundation") return iconTrust;
  return iconEntity;
}

function roleNames(parties) {
  return safeArr(parties)
    .map((p) => String(p?.role || "").trim())
    .filter(Boolean)
    .join(", ");
}

function partyNames(parties) {
  return safeArr(parties)
    .map((p) => String(p?.name || "").trim())
    .filter(Boolean)
    .join(", ");
}

function legalArrangementRoleLabel(party) {
  const role = String(party?.role || "").trim() || "—";
  if (role !== "Trustee") return role;

  const kind = String(party?.trusteeKind || "")
    .trim()
    .toLowerCase();
  if (kind === "company" || kind === "corporate") {
    return "Trustee (corporate)";
  }
  if (kind === "individual" || kind === "person" || kind === "natural person") {
    return "Trustee (individual)";
  }
  return role;
}

// function isBoParty(x, ubos) {
//   if (x?.isUBO === true || x?.isUbo === true || x?.bo === true) return true;

//   const name = personName(x).toLowerCase();
//   if (!name) return false;

//   return ubos.some((u) => personName(u).toLowerCase() === name);
// }

function InfoLine({
  icon,
  label,
  value,
  flagCode = "",
  useBlueIcon = false,
  iconBgClass = "",
  valueClassName = "",
  qualified = false,
}) {
  const empty = !String(value || "").trim();

  return (
    <div className="kycInfoItem">
      {/* <div className={useBlueIcon ? "kycInfoIconBlue" : "kycInfoIcon"}> */}
      <div
        className={
          `${
            iconBgClass
              ? iconBgClass
              : useBlueIcon
              ? "kycInfoIconBlue"
              : "kycInfoIcon"
          }${qualified ? " kycQualifiedUboAvatarIconBox" : ""}`
        }
      >
        <img src={icon} alt="" className="kycInfoIconImg" />
        {qualified ? (
          <img
            src={iconQualifiedUbo}
            alt="Qualified UBO"
            title="Qualified UBO"
            className="kycQualifiedUboAvatarBadge"
          />
        ) : null}
      </div>

      <div className="kycInfoContent">
        <div className="kycInfoViewLabel kycQualifiedUboLabel">
          <span>{label}</span>
        </div>

        {empty ? (
          <div className="kycInfoViewText">—</div>
        ) : (
          <div className={`kycInfoViewTextShareh ${valueClassName}`}>
            {flagCode ? (
              <img
                src={`https://flagcdn.com/w40/${String(flagCode)
                  .toLowerCase()
                  .trim()}.png`}
                alt=""
                className="kycRoundFlag"
                style={{ marginRight: 7 }}
              />
            ) : null}
            <span>{value}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function getRelatedShareholderLabel(
  rowId,
  shareholders,
  accountHolderName = ""
) {
  const id = String(rowId || "").trim();
  if (!id) return "—";
  if (id === "__other__") return "Other form of control";
  if (id === "__account_holder__") return accountHolderName || "Account holder";

  const found = (Array.isArray(shareholders) ? shareholders : []).find(
    (s) => String(s?._rowId || "") === id
  );

  return personName(found) || "—";
}

function trusteeDomicileForDirectTrustShareholder(item, legalArrangements = []) {
  const type = normType(
    item?.holderType || item?.type || item?.partyType || item?.shareholderType
  );
  if (type !== "trust") return "";

  const rowId = String(item?._rowId || "").trim();
  const name = personName(item).trim().toLowerCase();
  const arrangements = safeArr(legalArrangements);

  let arrangement = arrangements.find((arr) => {
    const arrType = normType(arr?.arrangementType || arr?.type);
    return (
      arrType === "trust" &&
      String(arr?.source || "").trim().toLowerCase() === "shareholder" &&
      String(arr?.sourceRefRowId || "").trim() === rowId
    );
  });

  if (!arrangement && name) {
    arrangement = arrangements.find((arr) => {
      const arrType = normType(arr?.arrangementType || arr?.type);
      return (
        arrType === "trust" &&
        String(arr?.name || "").trim().toLowerCase() === name
      );
    });
  }

  const trustee = safeArr(arrangement?.parties).find(
    (party) => String(party?.role || "").trim().toLowerCase() === "trustee"
  );
  return String(trustee?.domicile || "").trim();
}

function buildDirectShareholderUboRows(
  shareholders = [],
  { includeAllIndividuals = false } = {}
) {
  return safeArr(shareholders)
    .filter((s) => {
      const type = normType(
        s?.holderType || s?.type || s?.partyType || s?.shareholderType
      );
      if (type !== "individual") return false;

      if (includeAllIndividuals) return true;

      return s?.requiresDocuments === true;
    })
    .map((s, idx) => ({
      _rowId: `direct_sh_${String(s?._rowId || idx)}`,
      treeNodeId: String(s?.treeNodeId || s?.subjectTreeNodeId || s?.subjectNodeId || "").trim(),
      relatedShareholderRowId: "__account_holder__",
      name: personName(s) || "—",
      nationality: String(s?.nationality || "").trim(),
      countryOfResidence: String(s?.countryOfResidence || "").trim(),
      basisOfControl: "Direct ownership",
    }));
}

function hasMeaningfulShareholder(item) {
  if (!item || typeof item !== "object") return false;

  return Boolean(
    firstNonEmpty(
      item?.name,
      item?.fullName,
      item?.label,
      item?.holderType,
      item?.type,
      item?.partyType,
      item?.shareholderType,
      item?.nationality,
      item?.countryOfResidence,
      item?.ownershipPercent
    )
  );
}

function ShareholderCard({ item, ubos, legalArrangements = [], qualified = false, showPercent = true }) {
  // const type = normType(item?.type || item?.partyType || item?.shareholderType);
  // const isEntity = type === "entity";

  const type = normType(
    item?.holderType || item?.type || item?.partyType || item?.shareholderType
  );

  // const isEntity = type === "entity";
  const isEntity =
    type === "entity" || type === "trust" || type === "foundation";
  const isIndividual = type === "individual";
  const isTrust = type === "trust";
  const isFoundation = type === "foundation";

  const name = personName(item) || "—";

  const countryOfResidenceCode =
    trusteeDomicileForDirectTrustShareholder(item, legalArrangements) ||
    String(item?.countryOfResidence || "").trim();
  const nationalityCode = String(item?.nationality || "").trim();

  const domicileText = shortCountry(countryOfResidenceCode);
  const nationalityText = shortCountry(nationalityCode);
  const incorporationText = shortCountry(countryOfResidenceCode);
  // const domicileText = countryCodeToName(countryOfResidenceCode) || "—";
  // const nationalityText = countryCodeToName(nationalityCode) || "—";
  // const incorporationText = countryCodeToName(countryOfResidenceCode) || "—";

  // No % when the client is a PIC, passive holding, trust, foundation or quoted (not applicable).
  const pctText = showPercent ? formatPct(getPct(item)) : "N/A";

  const boText =
    String(item?.beneficialOwnerType || "").trim() === "Beneficial owner"
      ? "BO"
      : String(item?.beneficialOwnerType || "").trim() === "Nominee shareholder"
      ? "Nominee"
      : "—";

  const typeLabel =
    type === "entity"
      ? "Entity"
      : type === "trust"
      ? "Trust"
      : type === "foundation"
      ? "Foundation"
      : "Individual";

  const identityIcon =
    type === "individual"
      ? iconIndividual
      : type === "entity"
      ? iconEntity
      : iconTrust;

  // return (
  //   <div
  //     className="kycOwnershipPartyCard"
  //     style={{ backgroundColor: "#f9fbfd" }}
  //   >
  //     <div className="kycOwnershipGrid5">
  //       <div className="kycOwnershipCell">
  //         <InfoLine
  //           icon={identityIcon}
  //           label={typeLabel}
  //           value={name}
  //           useBlueIcon={true}
  //         />
  //       </div>

  //       <div className="kycOwnershipCell">
  //         <InfoLine icon={iconBo} label="Control Type" value={boText} />
  //       </div>

  //       <div className="kycOwnershipCell">
  //         <InfoLine icon={iconPercent} label="%" value={pctText} />
  //       </div>

  //       <div className="kycOwnershipCell">
  //         {isIndividual ? (
  //           <InfoLine
  //             icon={iconPassport}
  //             label="Nationality"
  //             value={nationalityText}
  //             flagCode={nationalityCode}
  //           />
  //         ) : (
  //           <div className="kycOwnershipPlaceholder" aria-hidden="true" />
  //         )}
  //       </div>

  //       <div className="kycOwnershipCell">
  //         <InfoLine
  //           icon={iconAddress}
  //           label={isEntity ? "Incorporation" : "Domicile"}
  //           value={isEntity ? incorporationText : domicileText}
  //           flagCode={countryOfResidenceCode}
  //         />
  //       </div>
  //     </div>
  //   </div>
  // );

  return (
    <div
      className="kycOwnershipPartyCard kycOwnership4ColCard kycShareholdersCard"
      // style={{ backgroundColor: "#FDFBF9" }}
    >
      {/* direct shareholders */}
      <div className="kycOwnershipCell kycOwnershipCellIdentity_beige">
        <InfoLine
          icon={identityIcon}
          label={typeLabel}
          value={name}
          iconBgClass="kycInfoIconDarkBeige"
          qualified={isIndividual && qualified}
          // useBlueIcon={true}
        />
      </div>

      <div className="kycOwnershipCell kycOwnershipCellShareholderControl">
        <div className="kycShareholderControlGrid">
          <div className="kycOwnershipCell">
            <InfoLine
              icon={iconBo}
              label="Control Type"
              value={boText}
              iconBgClass="kycInfoIconLightGrey"
            />
          </div>

          <div className="kycOwnershipCell">
            <InfoLine
              icon={iconPercent}
              label="%"
              value={pctText}
              iconBgClass="kycInfoIconLightGrey"
            />
          </div>
        </div>
      </div>

      <div className="kycOwnershipCell">
        {isIndividual ? (
          <InfoLine
            icon={iconPassport}
            label="Nationality"
            value={nationalityText}
            flagCode={nationalityCode}
            iconBgClass="kycInfoIconLightGrey"
          />
        ) : (
          <div className="kycOwnershipPlaceholder" aria-hidden="true" />
        )}
      </div>

      <div className="kycOwnershipCell">
        <InfoLine
          icon={iconAddress}
          label={isEntity ? "Incorporation" : "Domicile"}
          value={isEntity ? incorporationText : domicileText}
          flagCode={countryOfResidenceCode}
          iconBgClass="kycInfoIconLightGrey"
        />
      </div>
    </div>
  );

  // return (
  //   <div
  //     className="kycOwnershipPartyCard kycOwnershipPartyCardRow"
  //     style={{ backgroundColor: "#f9fbfd" }}
  //   >
  //     <div className="kycOwnershipCell kycOwnershipCellIdentity">
  //       <InfoLine
  //         icon={identityIcon}
  //         label={typeLabel}
  //         value={name}
  //         useBlueIcon={true}
  //       />
  //     </div>

  //     <div className="kycOwnershipCell kycOwnershipCellMedium">
  //       {isIndividual ? (
  //         <InfoLine
  //           icon={iconPassport}
  //           label="Nationality"
  //           value={nationalityText}
  //           flagCode={nationalityCode}
  //         />
  //       ) : (
  //         <div className="kycOwnershipPlaceholder" aria-hidden="true" />
  //       )}
  //     </div>

  //     <InfoLine
  //       icon={iconAddress}
  //       label={isEntity ? "Incorporation country" : "Domicile"}
  //       value={isEntity ? incorporationText : domicileText}
  //       flagCode={countryOfResidenceCode}
  //     />

  //     <div className="kycOwnershipCell kycOwnershipCellSmall">
  //       <InfoLine icon={iconBo} label="Control Type" value={boText} />
  //     </div>

  //     <div className="kycOwnershipCell kycOwnershipCellXSmall">
  //       <InfoLine icon={iconPercent} label="%" value={pctText} />
  //     </div>
  //   </div>
  // );
}

function groupUbosByRelatedShareholder(ubos = []) {
  const map = new Map();

  safeArr(ubos).forEach((u) => {
    const key = String(u?.relatedShareholderRowId || "").trim() || "__none__";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(u);
  });

  return Array.from(map.entries()).map(([relatedShareholderRowId, items]) => ({
    relatedShareholderRowId,
    items,
  }));
}

function UboGroupCard({
  relatedShareholderRowId,
  items,
  shareholders,
  accountHolderName,
  qualifiedNodeIds,
}) {
  const isAccountHolderGroup =
    String(relatedShareholderRowId || "").trim() === "__account_holder__";

  const relatedShareholder = getRelatedShareholderLabel(
    relatedShareholderRowId,
    shareholders,
    accountHolderName
  );

  // FDFBF9 f9fbfd

  return (
    <div className="kycOwnershipPartyCard kycLegalArrangementCard kycOwnership4ColCard kycUboCard">
      {/* UBOs */}
      <div className="kycOwnershipCell kycOwnershipCellIdentity_blue">
        <InfoLine
          icon={iconEntity}
          label={
            isAccountHolderGroup
              ? "Related account holder"
              : "Related shareholder"
          }
          value={relatedShareholder}
          iconBgClass="kycInfoIconDarkBlue"
          // useBlueIcon={true}
        />
      </div>

      <div className="kycOwnershipCell kycOwnershipCellLegalRows">
        {safeArr(items).length ? (
          <div className="kycLegalRowsWrap">
            {safeArr(items).map((p, idx) => {
              const basis = String(p?.basisOfControl || "").trim() || "—";
              const partyName = String(p?.name || "").trim() || "—";
              const nationalityCode = String(p?.nationality || "").trim();
              const domicileCode = String(p?.countryOfResidence || "").trim();

              const nationalityShort = shortCountry(nationalityCode);
              const domicileShort = shortCountry(domicileCode);

              return (
                <div
                  key={p?._rowId || `${basis}_${partyName}_${idx}`}
                  className="kycLegalPartyRow"
                >
                  {/* <div className="kycLegalPartyCell">
                    <InfoLine
                      icon={iconBo}
                      label="Basis of control"
                      value={shortBasisOfControl(basis)}
                    />
                  </div> */}

                  <div className="kycLegalPartyCell">
                    <InfoLine
                      icon={iconIndividual}
                      label={shortBasisOfControl(basis)}
                      // label="Natural person"
                      value={partyName}
                      iconBgClass="kycInfoIconGrey"
                      qualified={isQualifiedUboRow(p, qualifiedNodeIds)}
                    />
                  </div>

                  <div className="kycLegalPartyCell">
                    <InfoLine
                      icon={iconPassport}
                      label="Nationality"
                      value={nationalityShort}
                      flagCode={nationalityCode}
                      iconBgClass="kycInfoIconGrey"
                    />
                  </div>

                  <div className="kycLegalPartyCell">
                    <InfoLine
                      icon={iconAddress}
                      label="Domicile"
                      value={domicileShort}
                      flagCode={domicileCode}
                      iconBgClass="kycInfoIconGrey"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="kycViewTile">
            <div className="kycInfoViewText">—</div>
          </div>
        )}
      </div>
    </div>
  );
}

function roleIcon(role) {
  const r = String(role || "")
    .trim()
    .toLowerCase();

  if (r === "settlor") return iconSettlor;
  if (r === "trustee") return iconTrustee;
  if (r === "beneficiary") return iconBeneficiary;
  if (r === "protector") return iconProtector;
  if (r === "founder") return iconSettlor;
  if (r === "council member" || r === "council/board") return iconTrustee;
  if (r === "guardian") return iconSettlor;

  return iconIndividual; // any other role: generic person icon
}

function LegalArrangementCard({ item }) {
  const type = String(item?.arrangementType || "").trim();
  const arrangementName = String(item?.name || "").trim() || "—";
  const relatedShareholder =
    String(item?.relatedShareholderName || "").trim() || "—";

  const parties = safeArr(item?.parties);

  // FDFBF9 FBF9F7 f9fbfd

  return (
    <div
      // className="kycOwnershipPartyCard kycOwnershipPartyCardRow kycLegalArrangementCard"
      className="kycOwnershipPartyCard kycLegalArrangementCard kycOwnership4ColCard kycLegalArrangementViewCard"
    >
      {/* Legal arrangements */}
      <div className="kycOwnershipCell kycOwnershipCellIdentity_blue">
        <InfoLine
          icon={arrangementIcon(type)}
          label={arrangementTypeLabel(type)}
          value={relatedShareholder}
          // useBlueIcon={true}
          iconBgClass="kycInfoIconDarkBlue"
        />
      </div>

      {/* <div className="kycOwnershipCell kycOwnershipCellLarge">
        <div className="kycInfoItem">
          <div className="kycInfoIcon">
            <img src={iconEntity} alt="" className="kycInfoIconImg" />
          </div>

          <div className="kycInfoContent">
            <div className="kycInfoViewLabel">Related shareholder</div>
            <div className="kycInfoViewTextShareh">{relatedShareholder}</div>
          </div>
        </div>
      </div> */}

      <div className="kycOwnershipCell kycOwnershipCellLegalRows">
        {parties.length ? (
          <div className="kycLegalRowsWrap">
            {parties.map((p, idx) => {
              const role = String(p?.role || "").trim() || "—";
              const roleLabel = legalArrangementRoleLabel(p);
              const partyName = String(p?.name || "").trim() || "—";
              const nationalityCode = String(p?.nationality || "").trim();
              const domicileCode = String(p?.domicile || "").trim();
              const trusteeKind = String(p?.trusteeKind || "")
                .trim()
                .toLowerCase();
              const isCorporateTrustee =
                role === "Trustee" && trusteeKind === "company";

              const nationalityShort = isCorporateTrustee
                ? "N.A."
                : shortCountry(nationalityCode);
              const domicileShort = shortCountry(domicileCode);

              return (
                <div
                  key={p?._rowId || `${role}_${partyName}_${idx}`}
                  className="kycLegalPartyRow"
                >
                  {/* <div className="kycLegalPartyCell kycLegalPartyCellRole"> */}
                  <div className="kycLegalPartyCell">
                    <InfoLine
                      icon={roleIcon(role)}
                      label={roleLabel}
                      value={partyName}
                      iconBgClass="kycInfoIconGrey"
                    />
                  </div>

                  {/* <div className="kycLegalPartyCell kycLegalPartyCellNat"> */}
                  <div className="kycLegalPartyCell">
                    <InfoLine
                      icon={iconPassport}
                      label="Nationality"
                      value={nationalityShort}
                      flagCode={isCorporateTrustee ? "" : nationalityCode}
                      iconBgClass="kycInfoIconGrey"
                      valueClassName={isCorporateTrustee ? "text-muted" : ""}
                    />
                  </div>

                  {/* <div className="kycLegalPartyCell kycLegalPartyCellDom"> */}
                  <div className="kycLegalPartyCell">
                    <InfoLine
                      icon={iconAddress}
                      label="Domicile"
                      value={domicileShort}
                      flagCode={domicileCode}
                      iconBgClass="kycInfoIconGrey"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="kycViewTile">
            <div className="kycInfoViewText">—</div>
          </div>
        )}
      </div>
    </div>
  );
}

export function SignatoryCard({ item }) {
  const name = String(item?.name || "").trim() || "—";
  const capacity = normalizeSignatoryCapacity(item?.capacity) || "—";
  const nationalityCode = String(item?.nationality || "").trim();
  const residenceCode = String(item?.countryOfResidence || "").trim();
  const signatories = Array.isArray(item?._allSignatories) ? item._allSignatories : [];
  const signatoryNameById = new Map(
    signatories.map((row, idx) => [
      String(row?._rowId || `sig_idx_${idx}`),
      String(row?.name || "").trim() || `Signatory ${idx + 1}`,
    ])
  );
  const jointNames = Array.isArray(item?.jointWithSignatoryIds)
    ? item.jointWithSignatoryIds.map((id) => signatoryNameById.get(String(id))).filter(Boolean)
    : [];
  const signatureMode = item?.signatureMode === "joint" ? "joint" : "sole";
  const displayName =
    signatureMode === "joint" && jointNames.length && name !== "—"
      ? `${name} · joint with ${jointNames.join(", ")}`
      : name;

  const nationalityShort = shortCountry(nationalityCode);
  const residenceShort = shortCountry(residenceCode);

  return (
    <div className="kycOwnershipPartyCard kycSignatoriesCard">
      <div className="kycOwnershipGrid4">
        <div className="kycOwnershipCell">
          <InfoLine
            icon={iconIndividual}
            label="Signatory"
            value={displayName}
            iconBgClass="kycInfoIconDarkBeige"
            // useBlueIcon={true}
          />
        </div>

        <div className="kycOwnershipCell">
          <InfoLine
            icon={iconBo}
            label="Capacity"
            value={capacity}
            iconBgClass="kycInfoIconLightGrey"
          />
        </div>

        <div className="kycOwnershipCell">
          <InfoLine
            icon={iconPassport}
            label="Nationality"
            value={nationalityShort}
            flagCode={nationalityCode}
            iconBgClass="kycInfoIconLightGrey"
          />
        </div>

        <div className="kycOwnershipCell">
          <InfoLine
            icon={iconAddress}
            label="Domicile"
            value={residenceShort}
            flagCode={residenceCode}
            iconBgClass="kycInfoIconLightGrey"
          />
        </div>
      </div>
    </div>
  );
}

// *****************************************************************
// ***************** RETURN SHAREHOLDER FUNCTION *******************
// *****************************************************************

export default function KycViewOwnership({ sectionNo = 0, form }) {
  const { ownershipLabel } = useOwnershipLabels();
  const o = form?.ownership || {};
  const shareholders = safeArr(o.shareholders);
  const visibleShareholders = shareholders.filter(hasMeaningfulShareholder);
  const ubos = safeArr(o.ubos);
  const controllers = safeArr(o.controllers).length ? safeArr(o.controllers) : safeArr(o.signatories);
  const legalArrangements = safeArr(o?.legalArrangements?.arrangements);
  const qualifiedNodeIds = getQualifiedUboNodeIds(o);

  const uboFlags = o?.uboFlags || {};
  const isOperatingCompany =
    !uboFlags?.pctNotMeaningful &&
    !uboFlags?.quotedCompany &&
    !uboFlags?.accountHolderTrust &&
    !uboFlags?.accountHolderFoundation;

  const isPicOrPassiveHolding = !!uboFlags?.pctNotMeaningful;
  const isQuotedCompany = !!uboFlags?.quotedCompany;
  const isTrustAccountHolder = !!uboFlags?.accountHolderTrust;
  const isFoundationAccountHolder = !!uboFlags?.accountHolderFoundation;

  const accountHolderName =
    String(form?.customer?.name || "").trim() || "Account holder";
  const ownershipDiagramSnapshot =
    buildLiveReviewOwnershipSnapshot({
      ownership: o,
      accountHolderName,
    }) || o?.assistantTree || null;
  const hasOwnershipDiagram = !!ownershipDiagramSnapshot?.mermaid;

  // const preliminaryLabels = [
  //   isOperatingCompany ? "Operating Company" : null,
  //   isPicOrPassiveHolding ? "PIC / Passive Holding" : null,
  //   isQuotedCompany ? "Company quoted" : null,
  //   isTrustAccountHolder ? "Trust" : null,
  //   isFoundationAccountHolder ? "Foundation" : null,
  // ].filter(Boolean);

  const preliminarySentence = isPicOrPassiveHolding ? (
    <>
      {accountHolderName} is a{" "}
      <span className="kycPrelimHighlight">
        {ownershipLabel("personal investment company (PIC) or a passive holding")}
      </span>
      {" or a "}
      <span className="kycPrelimHighlight">passive holding</span> vehicle
      primarily used to hold investments and assets
    </>
  ) : isQuotedCompany ? (
    <>
      {accountHolderName} is a{" "}
      <span className="kycPrelimHighlight">
        company quoted on the stock exchange
      </span>
    </>
  ) : isTrustAccountHolder ? (
    <>
      {accountHolderName} is a <span className="kycPrelimHighlight">trust</span>
    </>
  ) : isFoundationAccountHolder ? (
    <>
      {accountHolderName} is a{" "}
      <span className="kycPrelimHighlight">foundation</span>
    </>
  ) : (
    <>
      {accountHolderName} is a{" "}
      <span className="kycPrelimHighlight">non quoted operating company</span>
      {" or an "}
      <span className="kycPrelimHighlight">active holding</span>
    </>
  );

  const preliminarySentence_OLD = isPicOrPassiveHolding
    ? `Account holder ${accountHolderName} is a personal investment company (PIC) or a passive holding vehicle primarily used to hold investments and assets`
    : isQuotedCompany
    ? `Account holder ${accountHolderName} is a company quoted on the stock exchange`
    : isTrustAccountHolder
    ? `Account holder ${accountHolderName} is a trust`
    : isFoundationAccountHolder
    ? `Account holder ${accountHolderName} is a foundation`
    : `Account holder ${accountHolderName} is a non quoted operating company or an active holding`;

  const directShareholderUbos = buildDirectShareholderUboRows(shareholders, {
    includeAllIndividuals: isPicOrPassiveHolding,
  });

  const ubosForView = [...directShareholderUbos, ...ubos];
  const groupedUbos = groupUbosByRelatedShareholder(ubosForView);
  const hasControllingPersons =
    groupedUbos.length > 0 || legalArrangements.length > 0;

  const toNames = (arr) =>
    arr.map((x) => x?.name || x?.fullName || x?.label || "").filter(Boolean);

  return (
    <div className="card kycCard">
      <div className={"kycSectionHead kycSectionHeadSection" + sectionNo}>
        <h3 className="kycSectionTitle">
          {sectionNo ? `${sectionNo} | ` : ""}Ownership & control
        </h3>
      </div>

      <div className="kycSectionBody">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          {/* <div className="kycMeta">Shareholders</div> */}

          {o?.complexStructure ? (
            <Badge text="Complex structure" tone="warn" />
          ) : null}
        </div>

        <div className="d-grid" style={{ gap: 14 }}>
          {/* ----- */}

          <div className="kycOwnershipSectionGroup">
            <div className="kycOwnershipSectionSubhead">
              {/* Preliminary Information */}
              Entity Type of the account holder
            </div>

            <div className="kycViewTile kycEntityType">
              {/* <div className="kycInfoViewLabel" style={{ marginBottom: 10 }}>
                Entity Type of the account holder
              </div> */}

              <div className="kycInfoViewText kycPrelimText">
                {preliminarySentence}
              </div>

              {/* {preliminaryLabels.length ? (
                <div className="kycViewPills">
                  {preliminaryLabels.map((label, idx) => (
                    <span key={idx} className="kycViewPill">
                      {label}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="kycInfoViewText">Operating Company</div>
              )} */}
            </div>
          </div>

          {hasOwnershipDiagram ? (
            <div className="kycOwnershipSectionGroup">
              <div className="kycOwnershipSectionSubhead">
                Ownership structure diagram
              </div>

              <div className="kycViewTile" style={{ padding: 14 }}>
                <OwnershipTreeUaeReadOnly snapshot={ownershipDiagramSnapshot} edgeLabelBg="#fffcfb" />
              </div>
            </div>
          ) : null}

          {/* ----- */}

          {visibleShareholders.length ? (
            <div className="kycOwnershipSectionGroup">
              <div className="kycOwnershipSectionSubhead">
                Direct Shareholders
              </div>

              {visibleShareholders.map((item, idx) => (
                <ShareholderCard
                  key={item?._rowId || item?.id || item?.name || idx}
                  item={item}
                  ubos={ubos}
                  legalArrangements={legalArrangements}
                  qualified={isQualifiedUboRow(item, qualifiedNodeIds)}
                  showPercent={clientCarriesPercent(o)}
                />
              ))}
            </div>
          ) : null}

          {/* ---- */}

          {hasControllingPersons ? (
            <div className="kycOwnershipSectionGroup">
              <div className="kycOwnershipSectionSubhead">
                Controlling persons
              </div>

              {groupedUbos.length ? (
                <div className="d-grid" style={{ gap: 14 }}>
                  <div className="kycInfoViewLabel" style={{ marginBottom: -2 }}>
                    UBOs
                  </div>

                  {groupedUbos.map((group, idx) => (
                    <UboGroupCard
                      key={group.relatedShareholderRowId || `ubo_group_${idx}`}
                      relatedShareholderRowId={group.relatedShareholderRowId}
                      items={group.items}
                      shareholders={shareholders}
                      accountHolderName={accountHolderName}
                      qualifiedNodeIds={qualifiedNodeIds}
                    />
                  ))}
                </div>
              ) : null}

              {legalArrangements.length ? (
                <div className="d-grid" style={{ gap: 14 }}>
                  <div className="kycInfoViewLabel" style={{ marginBottom: -2 }}>
                    Legal arrangements
                  </div>

                  {legalArrangements.map((item, idx) => (
                    <LegalArrangementCard
                      key={item?._rowId || item?.name || idx}
                      item={item}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
          {visibleShareholders.length ||
          controllers.length ||
          hasControllingPersons ? (
            <div style={{ height: 5 }}></div>
          ) : null}

          {/* ---- */}

          {/* <div className="kycOwnershipSectionGroup">
            <div className="kycOwnershipSectionSubhead">Signatories</div>

            {controllers.length ? (
              controllers.map((item, idx) => (
                <SignatoryCard
                  key={item?._rowId || item?.name || idx}
                  item={item}
                />
              ))
            ) : (
              <div className="kycViewTile">
                <div className="kycInfoViewText">—</div>
              </div>
            )}
          </div> */}
          {/* ---- */}
        </div>

        {/* ---- */}
      </div>
    </div>
  );
}
